import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import { tradeHistoryAPI } from "../services/tradeHistoryAPI";
import { goldTradeHistoryAPI } from "../services/goldTradeHistoryAPI";

const TradeLogs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("All Time");
  const [typeFilter, setTypeFilter] = useState("All"); // All, Gold, Bot
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const dateFilters = [
    "All Time",
    "Last 7 Days",
    "Last 30 Days",
    "Last 90 Days",
  ];
  const typeFilters = ["All", "Gold", "Bot"];
  const statusFilters = ["All", "Completed", "Pending", "Cancelled"];

  useEffect(() => {
    const fetchTrades = async () => {
      setLoading(true);
      try {
        let goldTrades = [];
        let botTrades = [];
        let startDate = "";
        let endDate = "";
        if (dateFilter !== "All Time") {
          const now = new Date();
          if (dateFilter === "Last 7 Days") {
            startDate = new Date(
              now.getTime() - 7 * 24 * 60 * 60 * 1000,
            ).toISOString();
          } else if (dateFilter === "Last 30 Days") {
            startDate = new Date(
              now.getTime() - 30 * 24 * 60 * 60 * 1000,
            ).toISOString();
          } else if (dateFilter === "Last 90 Days") {
            startDate = new Date(
              now.getTime() - 90 * 24 * 60 * 60 * 1000,
            ).toISOString();
          }
          endDate = now.toISOString();
        } else {
          startDate = undefined;
          endDate = undefined;
        }
        // Fetch gold trades
        if (typeFilter === "All" || typeFilter === "Gold") {
          const goldRes = await goldTradeHistoryAPI.getOrders({
            page: 1,
            limit: 100,
            startDate,
            endDate,
          });
          goldTrades = (goldRes.data || []).map((order) => ({
            id: order.id,
            date: order.createdAt
              ? new Date(order.createdAt).toLocaleDateString()
              : "",
            time: order.createdAt
              ? new Date(order.createdAt).toLocaleTimeString()
              : "",
            type: order.orderType || "Buy",
            asset: "XAU/USD",
            amount: order.ounces,
            price: `$${order.pricePerOz?.toLocaleString() ?? ""}`,
            pl: order.pnl ? `$${order.pnl.toLocaleString()}` : "-",
            plPositive: order.pnl > 0 ? true : order.pnl < 0 ? false : null,
            status:
              order.status?.charAt(0).toUpperCase() + order.status?.slice(1) ||
              "Completed",
            source: "Gold",
          }));
        }
        // Fetch bot trades
        if (typeFilter === "All" || typeFilter === "Bot") {
          const botRes = await tradeHistoryAPI.getHistory({
            page: 1,
            limit: 100,
            startDate,
            endDate,
          });
          botTrades = (botRes.trades || []).map((trade) => ({
            realizedNetProfit:
              typeof trade.realizedNetProfit === "number"
                ? trade.realizedNetProfit
                : trade.status === "completed"
                  ? Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0)
                  : null,
            id: trade.id,
            date: trade.createdAt
              ? new Date(trade.createdAt).toLocaleDateString()
              : "",
            time: trade.createdAt
              ? new Date(trade.createdAt).toLocaleTimeString()
              : "",
            type: "Bot",
            asset: trade.pair,
            amount: trade.investment,
            price: `$${trade.maxProfit?.toLocaleString() ?? ""}`,
            pl:
              (typeof trade.realizedNetProfit === "number" ||
                trade.status === "completed")
                ? `${(typeof trade.realizedNetProfit === "number"
                    ? trade.realizedNetProfit
                    : Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0)) >= 0 ? "+" : "-"}$${Math.abs(
                    typeof trade.realizedNetProfit === "number"
                      ? trade.realizedNetProfit
                      : Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0),
                  ).toLocaleString()}`
                : "-",
            plPositive:
              (typeof trade.realizedNetProfit === "number"
                ? trade.realizedNetProfit
                : trade.status === "completed"
                  ? Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0)
                  : null) > 0
                ? true
                : (typeof trade.realizedNetProfit === "number"
                    ? trade.realizedNetProfit
                    : trade.status === "completed"
                      ? Number(trade.maxProfit || 0) - Number(trade.estimatedFees || 0)
                      : null) < 0
                  ? false
                  : null,
            status:
              trade.status?.charAt(0).toUpperCase() + trade.status?.slice(1) ||
              "Pending",
            source: "Bot",
          }));
        }
        // Merge, sort by date desc, and filter
        let allTrades = [...goldTrades, ...botTrades];
        allTrades.sort((a, b) => {
          const aDate = new Date(a.date + " " + a.time);
          const bDate = new Date(b.date + " " + b.time);
          return bDate - aDate;
        });
        if (statusFilter !== "All") {
          allTrades = allTrades.filter((t) => t.status === statusFilter);
        }
        if (searchTerm) {
          allTrades = allTrades.filter(
            (t) =>
              t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (t.asset &&
                t.asset.toLowerCase().includes(searchTerm.toLowerCase())),
          );
        }
        // Pagination for merged results
        const pageSize = 10;
        setTotalPages(Math.max(1, Math.ceil(allTrades.length / pageSize)));
        const pagedTrades = allTrades.slice(
          (currentPage - 1) * pageSize,
          currentPage * pageSize,
        );
        setTrades(pagedTrades);
      } catch (e) {
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTrades();
  }, [typeFilter, statusFilter, dateFilter, searchTerm, currentPage]);
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-500/20 text-green-400 border border-green-500/30";
      case "Pending":
        return "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30";
      case "Cancelled":
        return "bg-red-500/20 text-red-400 border border-red-500/30";
      default:
        return "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-500/30";
    }
  };

  const getTypeBadgeClass = (type) => {
    return type === "Buy"
      ? "bg-[#22c55e]/20 text-primary border border-primary/30"
      : "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-500/30";
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 md:p-8 md:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Trade Logs
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Complete history of all your trading activities
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <svg
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-primary)] opacity-60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search Trade ID or Asset..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[var(--bg-card)] border border-primary/20 rounded-xl text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-primary transition-all"
            />
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="space-y-4">
            {/* Date Filters */}
            <div>
              <p className="text-sm text-[var(--text-primary)] opacity-60 mb-3 font-semibold">
                Date Range
              </p>
              <div className="flex flex-wrap gap-2">
                {dateFilters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setDateFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      dateFilter === filter
                        ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                        : "bg-transparent border border-primary/40 text-[var(--text-primary)] hover:bg-[#22c55e]/10"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {/* Type Filters */}
            <div>
              <p className="text-sm text-[var(--text-primary)] opacity-60 mb-3 font-semibold">
                Trade Type
              </p>
              <div className="flex flex-wrap gap-2">
                {typeFilters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setTypeFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      typeFilter === filter
                        ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                        : "bg-transparent border border-primary/40 text-[var(--text-primary)] hover:bg-[#22c55e]/10"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filters */}
            <div>
              <p className="text-sm text-[var(--text-primary)] opacity-60 mb-3 font-semibold">
                Status
              </p>
              <div className="flex flex-wrap gap-2">
                {statusFilters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setStatusFilter(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      statusFilter === filter
                        ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                        : "bg-transparent border border-primary/40 text-[var(--text-primary)] hover:bg-[#22c55e]/10"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <Card className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/10">
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Trade ID
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Date & Time
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Type
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Asset
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Amount (oz)
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Price
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    P/L
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-[var(--text-primary)]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade, index) => (
                  <tr
                    key={trade.id}
                    className="border-b border-primary/5 hover:bg-[#22c55e]/5 transition-all"
                  >
                    <td className="px-4 py-4">
                      <span className="text-sm text-[var(--text-primary)] font-mono">
                        {trade.id}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-[var(--text-primary)]">
                        <div>{trade.date}</div>
                        <div className="text-xs opacity-60">{trade.time}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeClass(
                          trade.type,
                        )}`}
                      >
                        {trade.type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-[var(--text-primary)] font-semibold">
                        {trade.asset}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-[var(--text-primary)]">{trade.amount}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-[var(--text-primary)] font-semibold">
                        {trade.price}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`text-sm font-bold ${
                          trade.plPositive === null
                            ? "text-[var(--text-primary)]"
                            : trade.plPositive
                              ? "text-green-400"
                              : "text-red-400"
                        }`}
                      >
                        {trade.pl}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                          trade.status,
                        )}`}
                      >
                        {trade.status}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => {
                          setSelectedTrade(trade);
                          setShowModal(true);
                        }}
                        className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-all"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {trades.map((trade) => (
            <Card
              key={trade.id}
              className="hover:transform hover:scale-[1.02] transition-all"
            >
              <div className="space-y-4">
                {/* Header Row */}
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">
                      Trade ID
                    </p>
                    <p className="text-sm text-[var(--text-primary)] font-mono font-semibold">
                      {trade.id}
                    </p>
                  </div>
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                      trade.status,
                    )}`}
                  >
                    {trade.status}
                  </span>
                </div>

                {/* Asset & Type Row */}
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">Asset</p>
                    <p className="text-base text-[var(--text-primary)] font-bold">
                      {trade.asset}
                    </p>
                  </div>
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeClass(
                      trade.type,
                    )}`}
                  >
                    {trade.type}
                  </span>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">Amount</p>
                    <p className="text-sm text-[var(--text-primary)] font-semibold">
                      {trade.amount} oz
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">Price</p>
                    <p className="text-sm text-[var(--text-primary)] font-semibold">
                      {trade.price}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">P/L</p>
                    <p
                      className={`text-sm font-bold ${
                        trade.plPositive === null
                          ? "text-[var(--text-primary)]"
                          : trade.plPositive
                            ? "text-green-400"
                            : "text-red-400"
                      }`}
                    >
                      {trade.pl}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-primary)] opacity-60 mb-1">
                      Date & Time
                    </p>
                    <p className="text-xs text-[var(--text-primary)]">{trade.date}</p>
                    <p className="text-xs text-[var(--text-primary)] opacity-60">{trade.time}</p>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => {
                    setSelectedTrade(trade);
                    setShowModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold rounded-xl hover:opacity-90 transition-all"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  View Details
                </button>
              </div>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-[var(--bg-card)] border border-primary/20 rounded-xl text-[var(--text-primary)] hover:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex gap-2">
            {[1, 2, 3].map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-xl font-semibold transition-all ${
                  currentPage === page
                    ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                    : "bg-[var(--bg-card)] border border-primary/20 text-[var(--text-primary)] hover:border-primary"
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(3, currentPage + 1))}
            disabled={currentPage === 3}
            className="px-4 py-2 bg-[var(--bg-card)] border border-primary/20 rounded-xl text-[var(--text-primary)] hover:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>

        {/* Footer */}
        <div className="text-center py-6 mt-8">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>

        {/* Trade Details Modal */}
        {showModal && selectedTrade && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--bg-card)] border border-primary/20 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-[#22c55e] to-[#16a34a] p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Trade Details</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-softer hover:bg-surface-soft transition-all"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Trade ID & Status */}
                <div className="flex items-center justify-between pb-4 border-b border-primary/10">
                  <div>
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Trade ID</p>
                    <p className="text-lg text-[var(--text-primary)] font-mono font-semibold">
                      {selectedTrade.id}
                    </p>
                  </div>
                  <span
                    className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadgeClass(selectedTrade.status)}`}
                  >
                    {selectedTrade.status}
                  </span>
                </div>

                {/* Trade Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-soft p-4 rounded-xl">
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Type</p>
                    <p className="text-lg text-[var(--text-primary)] font-semibold">
                      {selectedTrade.type}
                    </p>
                  </div>
                  <div className="bg-surface-soft p-4 rounded-xl">
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Pair</p>
                    <p className="text-lg text-[var(--text-primary)] font-semibold">
                      {selectedTrade.pair}
                    </p>
                  </div>
                  <div className="bg-surface-soft p-4 rounded-xl">
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Amount</p>
                    <p className="text-lg text-[var(--text-primary)] font-semibold">
                      {selectedTrade.amount}
                    </p>
                  </div>
                  <div className="bg-surface-soft p-4 rounded-xl">
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Price</p>
                    <p className="text-lg text-[var(--text-primary)] font-semibold">
                      {selectedTrade.price}
                    </p>
                  </div>
                  <div className="bg-surface-soft p-4 rounded-xl">
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Total</p>
                    <p className="text-lg text-[var(--text-primary)] font-semibold">
                      {selectedTrade.total}
                    </p>
                  </div>
                  <div className="bg-surface-soft p-4 rounded-xl">
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Fee</p>
                    <p className="text-lg text-[var(--text-primary)] font-semibold">
                      {selectedTrade.fee}
                    </p>
                  </div>
                </div>

                {/* Profit/Loss */}
                {selectedTrade.profit && (
                  <div
                    className={`p-4 rounded-xl ${selectedTrade.profit.startsWith("+") ? "bg-green-500/10 border border-green-500/20" : "bg-red-500/10 border border-red-500/20"}`}
                  >
                    <p className="text-sm text-[var(--text-secondary)] mb-1">Profit/Loss</p>
                    <p
                      className={`text-2xl font-bold ${selectedTrade.profit.startsWith("+") ? "text-green-400" : "text-red-400"}`}
                    >
                      {selectedTrade.profit}
                    </p>
                  </div>
                )}

                {/* Date & Time */}
                <div className="bg-surface-soft p-4 rounded-xl">
                  <p className="text-sm text-[var(--text-secondary)] mb-2">Timestamp</p>
                  <p className="text-lg text-[var(--text-primary)]">{selectedTrade.date}</p>
                  <p className="text-sm text-[var(--text-secondary)]">{selectedTrade.time}</p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-primary/10">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full py-3 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold rounded-xl hover:opacity-90 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeLogs;
